/**
 * Protections patrimoniales — Servitudes d'Utilité Publique de catégorie
 * "AC" qui touchent la parcelle.
 *
 *   AC1 — Abords de Monument Historique (périmètre 500 m, ou périmètre
 *         délimité s'il a été défini). Tout projet touchant à l'aspect
 *         extérieur du bien (façade, toiture, ouvertures, clôture) doit
 *         passer par l'avis conforme de l'ABF — délais réels 1 à 6 mois,
 *         refus fréquents pour fenêtres PVC, panneaux solaires visibles
 *         depuis le monument, etc.
 *   AC2 — Sites classés ou inscrits (loi de 1930). Procédure différente
 *         (autorisation ministérielle pour les sites classés, déclaration
 *         + avis ABF pour les inscrits) — beaucoup moins fréquente que AC1.
 *   AC4 — Site Patrimonial Remarquable (ancien AVAP/ZPPAUP). Règlement
 *         spécifique d'urbanisme + avis ABF systématique. C'est le plus
 *         contraignant des trois (souvent matériaux/colorimétrie imposés).
 *
 * Source : Géoportail de l'Urbanisme via apicarto IGN
 * (`/api/gpu/assiette-sup-s`). CORS ouvert, anonyme. On filtre côté client
 * pour ne garder que AC1/AC2/AC4 — les autres SUP (T1 ferroviaire,
 * I3 gaz, …) ne sont pas du red-flag patrimonial et sortiront dans un
 * autre bloc plus tard si pertinent.
 *
 * Stratégie de requête : on envoie la GÉOMÉTRIE de la parcelle quand on
 * l'a (intersection polygone/polygone, plus juste que centroïde — le
 * périmètre 500 m peut effleurer un coin de parcelle sans toucher le
 * centroïde). Fallback sur le point geocode sinon.
 *
 * TTL : 90 jours. Les SUP changent rarement (nouvel arrêté de protection
 * = procédure de plusieurs années) ; le ratio coût/fraîcheur penche pour
 * un cache long.
 */

import { useMemo } from "react";
import { useEnvBlock, coordKey } from "./shared";
import type { FicheDoc } from "../fiches";

const APICARTO_ASSIETTE_SUP =
  "https://apicarto.ign.fr/api/gpu/assiette-sup-s";
const ABF_TTL_DAYS = 90;

// ─── Types ───────────────────────────────────────────────────────────────

export type AbfSupType = "ac1" | "ac2" | "ac4";

export type AbfProtection = {
  // Catégorie SUP (ac1 / ac2 / ac4).
  suptype: AbfSupType;
  // Libellé court FR de la catégorie ("Abords de Monument historique", …).
  category: string;
  // Nom du monument / site / SPR. Décodé depuis `nomsuplitt` (qui arrive
  // parfois avec l'encodage cassé "HÃ´tel" → on tente une réparation).
  name: string;
  // Type d'assiette d'origine ("Monument historique", "Périmètre du SPR", …).
  // Utile pour distinguer un MH classé d'un MH inscrit dans le rendu.
  typeass: string | null;
  // Identifiant stable du générateur (= la source de la servitude). Utilisé
  // pour dédupliquer plusieurs assiettes d'un même MH.
  idgen: string;
  // PDF officiel (arrêté de protection). Première URL si plusieurs.
  fichierUrl: string | null;
};

export type AbfData = {
  // Nombre de protections AC distinctes touchant la parcelle.
  count: number;
  // Compteurs par type pour synthèse rapide.
  counts: { ac1: number; ac2: number; ac4: number };
  // Liste dédupliquée par `idgen`, triée par sévérité (ac4 > ac1 > ac2 —
  // l'AC4 est en général la plus contraignante, puis AC1, puis AC2).
  items: AbfProtection[];
  // Quelle géométrie a été envoyée à l'API : utile pour debug et pour
  // expliquer à l'utilisateur d'où vient la couverture.
  queryShape: "polygon" | "point";
};

// ─── Helpers ─────────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<AbfSupType, string> = {
  ac1: "Abords de Monument historique",
  ac2: "Site classé ou inscrit",
  ac4: "Site patrimonial remarquable",
};

const SEVERITY_ORDER: Record<AbfSupType, number> = {
  ac4: 0, // SPR — règlement spécifique, le plus contraignant
  ac1: 1, // Abords MH — avis ABF systématique
  ac2: 2, // Site classé/inscrit — pression patrimoniale moindre en général
};

function isAbfSupType(v: unknown): v is AbfSupType {
  return v === "ac1" || v === "ac2" || v === "ac4";
}

// `nomsuplitt` arrive parfois en latin-1 mal décodé ("HÃ´tel" pour "Hôtel").
// On tente une réparation best-effort : on encode-décode pour rétablir
// l'UTF-8. Si ça produit un truc qui n'est pas un vrai mot français, on
// retombe sur la valeur brute (mieux qu'un crash).
function repairMojibake(s: string): string {
  if (!/[ÃÂ]/.test(s)) return s; // pas le pattern typique mojibake → ne touche pas
  try {
    // Truc classique : prendre les codepoints comme s'ils étaient latin-1,
    // les ré-interpréter en UTF-8.
    const bytes = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i) & 0xff;
    const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    // Si la chaîne réparée contient toujours `Ã`, l'heuristique a échoué.
    if (/Ã/.test(decoded)) return s;
    return decoded;
  } catch {
    return s;
  }
}

// Extrait la première URL de PDF du champ `fichier` apicarto. Ce champ peut
// contenir plusieurs noms de fichiers séparés par `|`. Les fichiers sont
// servis sur https://www.geoportail-urbanisme.gouv.fr/document-document/{partition}/.
function buildFichierUrl(fichier: unknown, partition: unknown): string | null {
  if (typeof fichier !== "string" || fichier.length === 0) return null;
  if (typeof partition !== "string" || partition.length === 0) return null;
  const first = fichier.split("|")[0]?.trim();
  if (!first) return null;
  return `https://www.geoportail-urbanisme.gouv.fr/document-document/${encodeURIComponent(partition)}/${encodeURIComponent(first)}`;
}

// ─── Fetcher ─────────────────────────────────────────────────────────────

type ApicartoFeature = {
  type: "Feature";
  properties: Record<string, unknown>;
};
type ApicartoResponse = {
  type: "FeatureCollection";
  features: ApicartoFeature[];
};

export async function fetchAbf(geom: unknown): Promise<AbfData> {
  const geomJson = JSON.stringify(geom);
  const url = `${APICARTO_ASSIETTE_SUP}?geom=${encodeURIComponent(geomJson)}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(20_000),
  });
  if (!res.ok) {
    throw new Error(`apicarto SUP HTTP ${res.status}`);
  }
  const body = (await res.json()) as ApicartoResponse;
  const features = Array.isArray(body.features) ? body.features : [];

  // Dédup par idgen (= la source unique de la servitude). Un même monument
  // peut générer plusieurs assiettes (zone tampon différente, version
  // arrêté), on n'en garde qu'une — la plus précise (typeass non null).
  const byIdgen = new Map<string, AbfProtection>();
  for (const f of features) {
    const p = f.properties ?? {};
    const supt = typeof p.suptype === "string" ? p.suptype.toLowerCase() : null;
    if (!isAbfSupType(supt)) continue;

    const idgen = typeof p.idgen === "string" && p.idgen.length > 0
      ? p.idgen
      : `${supt}|${String(p.gid ?? Math.random())}`;
    const nameRaw = typeof p.nomsuplitt === "string" ? p.nomsuplitt : "";
    const name = repairMojibake(nameRaw).trim() || "Protection sans nom";
    const typeass = typeof p.typeass === "string" ? p.typeass : null;
    const fichierUrl = buildFichierUrl(p.fichier, p.partition);

    const entry: AbfProtection = {
      suptype: supt,
      category: CATEGORY_LABEL[supt],
      name,
      typeass,
      idgen,
      fichierUrl,
    };
    const existing = byIdgen.get(idgen);
    if (!existing) {
      byIdgen.set(idgen, entry);
      continue;
    }
    // On garde l'entrée avec le plus de signal (typeass renseigné, ou
    // fichierUrl renseigné).
    const score = (e: AbfProtection) =>
      (e.typeass ? 1 : 0) + (e.fichierUrl ? 1 : 0);
    if (score(entry) > score(existing)) {
      byIdgen.set(idgen, entry);
    }
  }

  const items = Array.from(byIdgen.values()).sort(
    (a, b) =>
      SEVERITY_ORDER[a.suptype] - SEVERITY_ORDER[b.suptype] ||
      a.name.localeCompare(b.name, "fr"),
  );
  const counts = { ac1: 0, ac2: 0, ac4: 0 };
  for (const it of items) counts[it.suptype]++;

  return {
    count: items.length,
    counts,
    items,
    // Caller fills queryShape outside the fetch — but we default to "point"
    // here for simplicity. The hook will overwrite via the wrapper.
    queryShape: "point",
  };
}

// ─── Hook ────────────────────────────────────────────────────────────────

/**
 * `useAbf` — protections AC autour de la parcelle.
 *
 * On envoie la géométrie de la parcelle (Polygon GeoJSON) si on l'a, sinon
 * on retombe sur le point geocode. Dans les deux cas, le centroïde du
 * geocode est utilisé pour `inputsKey` (pas la géométrie entière — on ne
 * veut pas que des micro-différences de digitalisation cadastrale bust le
 * cache).
 */
export function useAbf(fiche: FicheDoc | null) {
  const opts = useMemo(() => {
    if (!fiche?.geocode || !fiche.id) return null;
    const { lat, lng } = fiche.geocode;
    const parcelGeom = fiche.parcel?.geometry as unknown;
    const usePolygon =
      parcelGeom &&
      typeof parcelGeom === "object" &&
      "type" in parcelGeom &&
      ((parcelGeom as { type: string }).type === "Polygon" ||
        (parcelGeom as { type: string }).type === "MultiPolygon");

    return {
      ficheId: fiche.id,
      blockId: "abf" as const,
      inputsKey: `v1|${coordKey(lat, lng)}|${usePolygon ? "poly" : "pt"}`,
      ttlDays: ABF_TTL_DAYS,
      fetcher: async (): Promise<AbfData> => {
        const geom = usePolygon
          ? parcelGeom
          : { type: "Point", coordinates: [lng, lat] };
        const data = await fetchAbf(geom);
        return {
          ...data,
          queryShape: usePolygon ? "polygon" : "point",
        };
      },
    };
  }, [fiche?.id, fiche?.geocode, fiche?.parcel?.geometry]);

  return useEnvBlock<AbfData>(opts);
}
